import { APP_BASE_HREF } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideRouter, withInMemoryScrolling, withEnabledBlockingInitialNavigation } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { cacheInterceptor } from '@core/interceptors/cache.interceptor';
import { responseInterceptor } from '@core/interceptors/response.interceptor';
import { appRoutes } from './app.routes';
import { provideSocketIo } from 'ngx-socket-io';
import { environment } from '@environments/environment';
import { provideServiceWorker } from '@angular/service-worker';
import { provideNgxMask } from 'ngx-mask';
import { provideHighlightOptions } from 'ngx-highlightjs';
import {
	NbDatepickerModule,
	NbDialogModule,
	NbGlobalLogicalPosition,
	NbMenuModule,
	NbSidebarModule,
	NbThemeModule,
	NbTimepickerModule,
	NbToastrModule,
	NbWindowModule
} from '@nebular/theme';
import { DEFAULT_THEME } from '@styles/theme.default';
import { DARK_THEME } from '@styles/theme.dark';
import { COSMIC_THEME } from '@styles/theme.cosmic';
import { CORPORATE_THEME } from '@styles/theme.corporate';
import { MsalProviders } from '@core/msal.module';
import { MsalService } from '@azure/msal-angular';
import { UserService } from '@core/services/user.service';
import { legacyUrlInitializer } from '@core/initializers/legacy-url.initializer';
import { provideFormlyCore } from '@ngx-formly/core';
import { DATA_HUB_FORMLY_CONFIG } from '@core/utils/formly/formly.config';

export const appConfig: ApplicationConfig = {
	providers: [
		{ provide: APP_BASE_HREF, useValue: '/' },
		provideAppInitializer(legacyUrlInitializer()),
		provideAppInitializer(async () => {
			const msal = inject(MsalService);
			const user = inject(UserService);
			await msal.instance.initialize();
			await user.init();
		}),
		...MsalProviders,
		provideAnimations(),
		provideRouter(
			appRoutes,
			withEnabledBlockingInitialNavigation(),
			withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' })
		),
		provideHttpClient(
			withFetch(),
			withInterceptorsFromDi(),
			withInterceptors([responseInterceptor, cacheInterceptor])
		),
		provideServiceWorker('ngsw-worker.js', {
			enabled: environment.name !== 'local',
			registrationStrategy: 'registerWhenStable:30000'
		}),
		provideSocketIo({ url: environment.baseUrl, options: {} }),
		provideNgxMask(),
		importProvidersFrom(
			NbDatepickerModule.forRoot(),
			NbTimepickerModule.forRoot(),
			NbSidebarModule.forRoot(),
			NbWindowModule.forRoot(),
			NbMenuModule.forRoot(),
			NbDialogModule.forRoot({ closeOnBackdropClick: false, autoFocus: true }),
			NbThemeModule.forRoot({ name: 'default' }, [DEFAULT_THEME, DARK_THEME, COSMIC_THEME, CORPORATE_THEME]),
			NbToastrModule.forRoot({
				duration: 7000,
				preventDuplicates: true,
				duplicatesBehaviour: 'all',
				position: NbGlobalLogicalPosition.BOTTOM_END
			})
		),
		// chart.js is provided per-route by provideAppCharts() - see @core/providers/charts.provider
		provideFormlyCore(DATA_HUB_FORMLY_CONFIG),
		provideHighlightOptions({
			coreLibraryLoader: () => import('highlight.js/lib/core'),
			lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'),
			languages: {
				xml: () => import('highlight.js/lib/languages/xml'),
				json: () => import('highlight.js/lib/languages/json'),
				ebnf: () => import('highlight.js/lib/languages/ebnf')
			}
			// themePath: 'atom-one-dark.css'
		})
	]
};
